import {useEffect, useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {find} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerV2Query, useGetVehicleCustomerQuery} from '@dms/api/customer';
import {useGetMetadaServiceCaseQuery} from '@dms/api/workshop';

interface UseDriverCustomerDataProps<T extends {driverId?: string | null}> {
  serviceCaseId: string;
  formApi: UseFormReturn<T>;
}

export function useDriverCustomerData<T extends {driverId?: string | null}>(
  props: UseDriverCustomerDataProps<T>
) {
  const {serviceCaseId, formApi} = props;

  const [driverId, setDriverId] = useState<string | null | undefined>(
    formApi.getValues().driverId ?? null
  );

  useEffect(() => {
    const subscription = formApi.watch((value) => {
      setDriverId(value.driverId ?? null);
    });
    return () => subscription.unsubscribe();
  }, [formApi]);

  const {data: serviceCase} = useGetMetadaServiceCaseQuery(
    {serviceCaseId},
    {skip: isNilOrEmpty(serviceCaseId)}
  );

  const {data: vehicleCustomers} = useGetVehicleCustomerQuery(
    {vehicleId: serviceCase?.vehicleId ?? ''},
    {skip: isNilOrEmpty(serviceCase?.vehicleId)}
  );

  const vehicleUser = find(
    (vehicleCustomer) => vehicleCustomer?.customerType === 'CORVEHICLECUSTOMERTYPE_USER',
    vehicleCustomers ?? []
  );

  const fleetManager = find(
    (vehicleCustomer) => vehicleCustomer?.customerType === 'CORVEHICLECUSTOMERTYPE_MANAGER',
    vehicleCustomers ?? []
  );

  const vehicleOperator = find(
    (vehicleCustomer) => vehicleCustomer?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR',
    vehicleCustomers ?? []
  );

  const fallbackVehicleCustomer = vehicleUser ?? fleetManager ?? vehicleOperator;

  const effectiveCustomerId = driverId ?? fallbackVehicleCustomer?.customerId ?? '';

  const {data: customerData} = useGetCustomerV2Query(
    {customerId: effectiveCustomerId},
    {skip: isNilOrEmpty(effectiveCustomerId)}
  );

  const {data: fallbackCustomerData} = useGetCustomerV2Query(
    {customerId: fallbackVehicleCustomer?.customerId ?? ''},
    {skip: isNilOrEmpty(fallbackVehicleCustomer?.customerId)}
  );

  return {
    customerData: customerData ?? null,
    fallbackCustomerData: fallbackCustomerData ?? null,
  };
}
